import type { ReactNode } from "react"
import { Badge, type BadgeVariant } from "./ui/badge"
import type { LucideIcon } from "lucide-react"
import { cn } from "../lib/utils"

const STATUS_VARIANT_VALUES = {
  success: [
    "active",
    "adopted_live",
    "advance",
    "approved",
    "approved_pending_publish",
    "automation_active",
    "completed",
    "enabled",
    "handoff_ready",
    "healthy",
    "ingested",
    "live",
    "published",
    "published_live",
    "ready",
    "review_ready",
    "submitted",
    "supported",
    "succeeded",
    "verified_ready",
  ],
  warning: [
    "attention",
    "awaiting_desktop",
    "changes_pending",
    "completed_with_errors",
    "completed_with_warnings",
    "high",
    "hold",
    "needs_review",
    "partial",
    "preview_opt_in",
    "preview_rollout",
    "provisioning",
    "ready_for_review",
    "retiring",
    "succeeded_with_warnings",
    "upcoming",
  ],
  primary: ["cooling", "medium"],
  destructive: [
    "blocked",
    "breaking",
    "changes_requested",
    "closed",
    "deprecated",
    "disabled",
    "error",
    "expired",
    "fact_check_failed",
    "failed",
    "needs_setup",
    "publish_failed",
    "reject",
    "rejected",
    "retired",
    "revoked",
    "validation_failed",
  ],
  default: [
    "archived",
    "automation_paused",
    "canceled",
    "cancelled",
    "draft",
    "draft_only",
    "duplicate",
    "editing",
    "inactive",
    "in_progress",
    "invited",
    "low",
    "none",
    "not_adopted",
    "not_applicable",
    "not_ready",
    "not_required",
    "not_run",
    "not_started",
    "not_submitted",
    "paused",
    "paused_by_dataset",
    "pending",
    "pinned_version",
    "processing",
    "publishing",
    "queued",
    "ready_for_publish",
    "running",
    "sent",
    "skipped",
    "suggested",
    "superseded",
    "unknown",
    "verified",
    "withheld",
  ],
} satisfies Record<BadgeVariant, readonly string[]>

const STATUS_VARIANTS = createStatusVariantMap(STATUS_VARIANT_VALUES)

const STATUS_LABELS: Record<string, string> = {
  adoptedlive: "Adopted live",
  approvedpendingpublish: "Approved pending publish",
  automationactive: "Automation Active",
  automationpaused: "Automation Paused",
  awaitingdesktop: "Awaiting desktop",
  changespending: "Changes pending",
  changesrequested: "Changes requested",
  completedwitherrors: "Completed with Errors",
  completedwithwarnings: "Completed with Warnings",
  draftonly: "Draft only",
  factcheckfailed: "Fact-check Failed",
  handoffready: "Handoff ready",
  inprogress: "In Progress",
  needsreview: "Needs review",
  needssetup: "Needs setup",
  notadopted: "Not adopted",
  notready: "Not ready",
  notrequired: "No active review",
  notrun: "Not run",
  notstarted: "Not started",
  notsubmitted: "Not submitted",
  pausedbydataset: "Paused by Dataset",
  previewoptin: "Preview opt-in",
  previewrollout: "Preview rollout",
  publishfailed: "Publish Failed",
  publishedlive: "Published live",
  readyforpublish: "Suggested",
  readyforreview: "Ready for review",
  reviewready: "Review ready",
  succeededwithwarnings: "Succeeded with Warnings",
  validationfailed: "Validation failed",
  verifiedready: "Verified ready",
}

export type StatusBadgeProps = {
  className?: string
  icon?: LucideIcon
  iconClassName?: string
  label?: ReactNode
  status: string | ReactNode
  secondaryLabel?: ReactNode
  uppercase?: boolean
  variant?: BadgeVariant
}

export function StatusBadge({
  className,
  icon,
  iconClassName,
  label,
  status,
  secondaryLabel,
  uppercase,
  variant,
}: StatusBadgeProps) {
  const statusVariant = variant || getStatusBadgeVariant(status)
  const Icon = icon
  const displayLabel =
    label !== undefined
      ? label
      : typeof status === "string"
        ? getFriendlyStatusLabel(status)
        : status

  return (
    <Badge
      variant={statusVariant}
      className={cn("min-w-max", className, {
        "ds-type-tracking-normal ds-type-uppercase": uppercase,
      })}
      leftIcon={
        Icon ? <Icon className={cn("size-3", iconClassName)} /> : undefined
      }
      secondaryLabel={secondaryLabel}
    >
      {displayLabel}
    </Badge>
  )
}

export function getStatusBadgeVariant(
  status: string | ReactNode
): BadgeVariant {
  if (typeof status !== "string") {
    return "default"
  }

  return STATUS_VARIANTS[normalizeStatusKey(status)] ?? "default"
}

export function isKnownStatusBadgeValue(status: string | ReactNode): boolean {
  return (
    typeof status === "string" && normalizeStatusKey(status) in STATUS_VARIANTS
  )
}

function getFriendlyStatusLabel(status: string): string {
  const normalized = normalizeStatusKey(status)
  const explicitLabel = STATUS_LABELS[normalized]

  if (explicitLabel) {
    return explicitLabel
  }

  const spaced = status
    .trim()
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")

  return spaced
}

function createStatusVariantMap(
  groups: Record<BadgeVariant, readonly string[]>
) {
  const entries: Array<[string, BadgeVariant]> = []

  for (const [variant, statuses] of Object.entries(groups) as Array<
    [BadgeVariant, readonly string[]]
  >) {
    for (const status of statuses) {
      entries.push([normalizeStatusKey(status), variant])
    }
  }

  return Object.fromEntries(entries) as Record<string, BadgeVariant>
}

function normalizeStatusKey(status: string) {
  return status.toLowerCase().replace(/[^a-z0-9]/g, "")
}
